import React, { useEffect, useRef, useState } from "react";
import { InputProp } from "../../utils/tsConfig";

const Bool: React.FC<InputProp> = ({
  setState,
  state,
  isArray,
  arrayName,
  indexVal,
  returnKey,
  fieldName,
  className = "",
  required = false,
  disabled = false,
  onChange,
  keyId,
}) => {
  const [selected, setSelected] = useState<boolean | null>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const options = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  useEffect(() => {
    let val;
    if (isArray && arrayName && indexVal !== undefined && returnKey && state) {
      val = state?.[arrayName]?.[indexVal]?.[returnKey];
    } else if (returnKey && state) {
      val = state[returnKey];
    } else {
      val = state;
    }

    if (typeof val === "boolean") {
      setSelected(val);
    } else {
      setSelected(null);
    }
  }, [state, isArray, arrayName, indexVal, returnKey]);

  const handleSelect = (option: { label: string; value: boolean }) => {
    setSelected(option.value);
    setIsDropdownVisible(false);
    setSearchTerm("");

    let newState;
    if (isArray && arrayName && indexVal !== undefined && returnKey && setState) {
      const updated = { ...state };
      if (!updated[arrayName]) updated[arrayName] = [];
      while (updated[arrayName].length <= indexVal) {
        updated[arrayName].push({});
      }
      updated[arrayName][indexVal][returnKey] = option.value;
      newState = updated;
    } else if (returnKey && setState) {
      newState = { ...state, [returnKey]: option.value };
    } else {
      newState = option.value;
    }

    setState?.(newState);
    onChange?.({ value: option.value.toString() });
  };

  const displayValue = selected === true ? "Yes" : selected === false ? "No" : "Select...";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <label key={keyId} className={`block ${className}`}>
      <div className="relative" ref={dropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {fieldName?.replace(/\*$/, "")}
          {fieldName?.endsWith("*") && <span className="text-red-500">*</span>}
        </label>

        <div
          className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm cursor-pointer ${
            disabled ? "bg-gray-100 text-gray-500" : "hover:border-indigo-500"
          } sm:text-sm`}
          onClick={() => !disabled && setIsDropdownVisible(!isDropdownVisible)}
        >
          {displayValue}
        </div>

        {isDropdownVisible && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 border-b border-gray-300 text-sm focus:outline-none"
            />
            <ul className="focus:outline-none list-none sm:text-sm">
              {filteredOptions.map((option, index) => (
                <li
                  key={index}
                  onClick={() => handleSelect(option)}
                  className="flex items-center py-2 m-1 cursor-pointer hover:bg-indigo-100 rounded-md"
                >
                  <div className="flex h-5 items-center w-full">
                    <div className="text-sm font-medium">{option.label}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </label>
  );
};

export default Bool;
